import React from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import {
  TextInput,
  SelectInput,
  DateInput,
  AutoCompleteInput,
} from '@/shared/components/forms';
import { UseFormReturnType } from '@refinedev/react-hook-form';
import { HttpError } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import {
  ParticipantProfile,
  UpdateParticipant,
  genderValues,
  ChronicIllness,
  notificationPreferences,
  PREDEFINED_CHRONIC_ILLNESSES,
  getKey,
} from '@aphp-ema/common/participant';
import { getDisplayName } from '@/features/participants/utils/chronic-illness';

interface ParticipantFormProps {
  form: UseFormReturnType<ParticipantProfile, HttpError, UpdateParticipant>;
}

export const ParticipantForm: React.FC<ParticipantFormProps> = ({ form }) => {
  const { t } = useTranslation();
  const { t: tChronicIllnesses } = useTranslation('chronicIllnesses');
  // Get readonly values from query data
  const record = form.refineCore.query?.data?.data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {/* Readonly fields */}
      <TextField
        label="EMA ID"
        value={record?.emaId || ''}
        disabled
        fullWidth
      />

      {/* Editable fields */}
      <TextInput
        form={form}
        name="email"
        label={t('pages.participants.fields.email')}
        type="email"
      />

      <TextInput
        form={form}
        name="phone"
        label={t('pages.participants.fields.phone')}
        type="tel"
      />

      {/* Patient Data fields */}
      <DateInput
        form={form}
        name="patientData.birthdate"
        label={t('pages.participants.fields.birthdate')}
      />

      <SelectInput
        form={form}
        name="patientData.gender"
        label={t('pages.participants.fields.gender')}
      >
        {genderValues.map((gender) => (
          <MenuItem key={gender} value={gender}>
            {t(`pages.participants.gender.${gender}`)}
          </MenuItem>
        ))}
      </SelectInput>

      <SelectInput
        form={form}
        name="notificationPreference"
        label={t('pages.participants.notificationPreference.title')}
      >
        {notificationPreferences.map((preference) => (
          <MenuItem key={preference} value={preference}>
            {t(`pages.participants.notificationPreference.${preference}`)}
          </MenuItem>
        ))}
      </SelectInput>

      <AutoCompleteInput
        form={form}
        name="patientData.chronicIllnesses"
        label={t('pages.participants.fields.chronicIllnesses')}
        options={PREDEFINED_CHRONIC_ILLNESSES}
        multiple
        getOptionLabel={(option) =>
          getDisplayName(option as ChronicIllness, tChronicIllnesses)
        }
        getOptionKey={(option) => getKey(option as ChronicIllness)}
      />
    </Box>
  );
};
